import { createScanner } from 'jsonc-parser';

import { isTrivia, ScanErrorKind, type Token, TokenKind } from './tokens';

/**
 * The token stream. Trivia included, errors reported as data, never thrown (ADR-0007).
 *
 * This is the substrate under lint, repair and the fidelity printer, so it has two
 * properties the rest of the engine relies on:
 *
 * 1. **It is total.** Unterminated strings, invalid escapes, half-written numbers, raw
 *    control characters and arbitrary garbage all come back as tokens carrying a
 *    {@link ScanErrorKind}. `tokenize` has no failure mode — the caller always gets a
 *    stream to reason about, which is the whole reason a broken document can still be
 *    linted, repaired and pretty-printed.
 * 2. **It keeps trivia.** Whitespace, line breaks and comments are tokens like any other.
 *    The repair rules need to know what sits between two values (ADR-0011) and the printer
 *    needs to know what it is allowed to regenerate (ADR-0013). A stream with trivia
 *    stripped cannot answer either question.
 *
 * This file is one of the three allowed to touch `jsonc-parser` (ADR-0007), and it does so
 * through the local structural types below rather than the library's own ambient
 * `const enum`s, which do not compile under `verbatimModuleSyntax` (ADR-0022).
 */

/**
 * `jsonc-parser`'s `JSONScanner`, restated with plain `number`s.
 *
 * The library types `scan()` as returning `SyntaxKind` and `getTokenError()` as returning
 * `ScanError` — both ambient `const enum`s. Naming them is `TS2748`; describing the same
 * runtime object with `number` is not, and it keeps the numeric knowledge of jsonc inside
 * this file and `tokens.ts` exactly as ADR-0022 requires.
 */
interface NumericScanner {
  scan(): number;
  getTokenOffset(): number;
  getTokenLength(): number;
  getTokenError(): number;
}

/** The single cast at the library seam. Everything below it is our own vocabulary. */
const createNumericScanner = createScanner as unknown as (
  text: string,
  ignoreTrivia?: boolean,
) => NumericScanner;

/**
 * Scan `source` into tokens with exact half-open offsets in UTF-16 code units.
 *
 * The returned array **always ends with a `TokenKind.EOF` token**, zero width, at
 * `source.length`. It is a sentinel, not an accident: rules that ask "what follows this
 * comma?" never have to special-case the end of the document, and `tokens.at(-1)!.to` is
 * always the length of the input. It is the only zero-width token in the stream.
 *
 * A byte-order mark is *not* special-cased here — jsonc scans it as
 * `TokenKind.Unknown` at offset 0, and the lexer reports what it sees. Stripping the BOM
 * is the precheck's job, one step earlier in the pipeline (ADR-0007 step 0).
 *
 * `getTokenValue()` is deliberately never called: for comment tokens it returns a value
 * with stray leading whitespace, so the rule across the whole engine is to slice the
 * source by `from`/`to` instead (ADR-0007).
 */
export function tokenize(source: string): Token[] {
  const scanner = createNumericScanner(source, /* ignoreTrivia */ false);
  const tokens: Token[] = [];

  // Every token but EOF consumes at least one code unit, so a document of n code units
  // yields at most n tokens plus the sentinel. Stating that bound as the loop condition
  // makes termination a property of the code rather than a property of jsonc's internals.
  const maxTokens = source.length + 1;

  while (tokens.length < maxTokens) {
    const kind = scanner.scan() as TokenKind;

    // Clamping is not paranoia — jsonc 3.3.1 has a measured off-by-one. A block comment
    // opened and never closed, with an EMPTY body, runs one code unit past the end:
    // `tokenize('/*')` gets `offset 0, length 3` for a two-unit document, and the EOF that
    // follows is reported at `offset 2, length 1`. (`/*a` and `/* hi` are correct; only the
    // empty body overruns.) An out-of-range `to` would survive all the way into a
    // `TextEdit`, where `applyTextEdits` would splice past the end of the user's document.
    // The substrate absorbs it here, once, instead of every consumer learning about it.
    const from = Math.min(scanner.getTokenOffset(), source.length);

    tokens.push({
      kind,
      from,
      to: Math.min(from + scanner.getTokenLength(), source.length),
      scanError: scanner.getTokenError() as ScanErrorKind,
    });

    if (kind === TokenKind.EOF) return tokens;
  }

  // Unreachable with jsonc 3.3.1; kept so that the EOF sentinel is a guarantee of the
  // signature and not of a dependency.
  tokens.push({
    kind: TokenKind.EOF,
    from: source.length,
    to: source.length,
    scanError: ScanErrorKind.None,
  });
  return tokens;
}

/**
 * The stream with trivia dropped and the EOF sentinel kept.
 *
 * For rules that reason about grammar ("is the token before this brace a comma?") and do
 * not care how much whitespace sits in between. Rules that *do* care — every layout rule
 * in the printer — keep using the full stream.
 */
export function significantTokens(tokens: readonly Token[]): Token[] {
  return tokens.filter((token) => !isTrivia(token));
}
